// src/content/ReporteriaContainer/ReportEditContainer/index.js
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { 
  Box, 
  Card, 
  Typography, 
  Button, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  Radio, 
  FormControlLabel, 
  Checkbox, 
  Select, 
  MenuItem, 
  TextField, 
  Divider, 
  Grid,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse
} from '@material-ui/core';
import { 
  ArrowBack, 
  Add as AddIcon, 
  Close as CloseIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  FileCopy as FileCopyIcon,
  FlashOn as FlashOnIcon,
  Schedule as ScheduleIcon
} from '@material-ui/icons';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import ReporteriaAPI from '../reporteria-api';
import LoadingModal from '../ReportsList/reportModal';
import {
  CAMPOS_REGISTRO,
  CAMPOS_ACTIVIDAD,
  getAllowedFieldNames,
  normalizeFieldSelection,
} from '../reportFieldCatalog';

// Opciones de días de semana para selección múltiple
const DIAS_SEMANA = [
  { id: '1', label: 'Lunes' },
  { id: '2', label: 'Martes' },
  { id: '3', label: 'Miércoles' },
  { id: '4', label: 'Jueves' },
  { id: '5', label: 'Viernes' },
  { id: '6', label: 'Sábado' },
  { id: '0', label: 'Domingo' }
];

// Opciones de días del mes para selección (1-31)
const DIAS_MES = Array.from({ length: 31 }, (_, i) => i + 1);

// Meses con 30 días
const MESES_30 = [4, 6, 9, 11]; // Abril, Junio, Septiembre, Noviembre

// Función para verificar si un día existe en todos los meses
const existeEnTodosLosMeses = (dia) => {
  return dia <= 28; // Solo los días 1-28 existen en todos los meses
};

// Función para obtener advertencia según el día del mes
const getAdvertenciaDiaMes = (dia) => {
  if (dia <= 28) return null; // Días 1-28 existen en todos los meses
  if (dia === 29) return "El día 29 no existe en febrero en años no bisiestos";
  if (dia === 30) return "El día 30 no existe en febrero";
  if (dia === 31) return "El día 31 no existe en febrero, abril, junio, septiembre y noviembre";
  return null;
};

const ReportEditContainer = () => {
  const history = useHistory();
  const { reportId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [error, setError] = useState(null);
  
  // Funciones helper para manejo de tiempo
  const createTimeFromHora = (horaString) => {
    const [hours, minutes] = horaString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const extractHoraFromTime = (timeDate) => {
    if (!timeDate) return '12:00';
    return moment(timeDate).format('HH:mm');
  };

  const shouldDisableTime = (timeValue, clockType) => {
    if (clockType === 'minutes') {
      return timeValue !== 0; // Solo permitir minutos = 00
    }
    return false;
  };
  
  // Estados del formulario
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [tipoReporte, setTipoReporte] = useState('register');
  const [camposSeleccionados, setCamposSeleccionados] = useState(() => normalizeFieldSelection(CAMPOS_REGISTRO));
  const [filtros, setFiltros] = useState({
    role: [],
    rubro: [],
    genero: [],
    region: [],
    convocatoria: []
  });
  const [modoCreacion, setModoCreacion] = useState('scheduled'); // 'instant' o 'scheduled'
  const [allowInstant, setAllowInstant] = useState(true); // Permitido cambiar a instant?
  const [frecuencia, setFrecuencia] = useState('once');
  const [diasSemana, setDiasSemana] = useState(['1']); // Lunes por defecto, ahora es array para múltiples
  const [diaMes, setDiaMes] = useState(1); // Día 1 por defecto
  const [fechaProgramada, setFechaProgramada] = useState(new Date()); // Para programar "una sola vez"
  const [hora, setHora] = useState('12:00');
  const [timePickerValue, setTimePickerValue] = useState(createTimeFromHora('12:00'));
  const [emails, setEmails] = useState([]);
  const [nuevoEmail, setNuevoEmail] = useState('');
  const [previewJson, setPreviewJson] = useState(null);
  
  // Estados para los datos de los filtros
  const [regiones, setRegiones] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [generos, setGeneros] = useState([]);
  const [convocatorias, setConvocatorias] = useState([]);

  // Estados de carga para cada filtro
  const [isLoadingRegiones, setIsLoadingRegiones] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingRubros, setIsLoadingRubros] = useState(true);
  const [isLoadingGeneros, setIsLoadingGeneros] = useState(true);
  const [isLoadingConvocatorias, setIsLoadingConvocatorias] = useState(true);  
 
  // Estados de error para cada filtro
  const [errorRegiones, setErrorRegiones] = useState(null);
  const [errorRoles, setErrorRoles] = useState(null);
  const [errorRubros, setErrorRubros] = useState(null);
  const [errorGeneros, setErrorGeneros] = useState(null);
  const [errorConvocatorias, setErrorConvocatorias] = useState(null);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Cargar el reporte existente
  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await ReporteriaAPI.getReportById(reportId);
        console.log('Respuesta del backend:', response);
        
        if (!response || !response.data) {
          throw new Error('No se recibieron datos del reporte');
        }

        const reportData = response.data.reporte;
        
        if (!reportData) {
          throw new Error('No se encontró la información del reporte');
        }

        // La configuración está en el campo configuration
        const config = reportData.configuration || {};
        const schedule = reportData.schedule || {};

        // Actualizar los estados con los datos del reporte
        setReportName(reportData.name || '');
        setReportDescription(reportData.description || '');
        const selectedTipoReporte = config.tipo || 'register';
        const allowedFieldNames = getAllowedFieldNames(selectedTipoReporte);
        setTipoReporte(selectedTipoReporte);
        setCamposSeleccionados(
          normalizeFieldSelection(config.campos, allowedFieldNames)
        );
        // Asegurar que todos los filtros sean arrays válidos
        const filtrosFromConfig = config.filtros || {};
        console.log('Filtros desde la configuración:', filtrosFromConfig);
        setFiltros({
          role: Array.isArray(filtrosFromConfig.role) ? filtrosFromConfig.role : [],
          rubro: Array.isArray(filtrosFromConfig.rubro) ? filtrosFromConfig.rubro : [],
          genero: Array.isArray(filtrosFromConfig.genero) ? filtrosFromConfig.genero : [],
          region: Array.isArray(filtrosFromConfig.region) ? filtrosFromConfig.region : [],
          convocatoria: Array.isArray(filtrosFromConfig.convocatoria) ? filtrosFromConfig.convocatoria : []
        });

        // Configurar la frecuencia y programación
        if (schedule.tipo) {
          // Determinar el modo de creación basado en el tipo de schedule
          if (schedule.tipo === 'instant') {
            setModoCreacion('instant');
            setAllowInstant(true);
          } else {
            setModoCreacion('scheduled');
            setAllowInstant(false); // Reporte programado, no permitir pasar a instant
          }
          
          setFrecuencia(schedule.tipo);
          
          if (schedule.tipo === 'once') {
            // Parse fecha de forma segura en local (YYYY-MM-DD)
            const [y,m,d] = schedule.config.fecha.split('-').map(Number);
            setFechaProgramada(new Date(y, m - 1, d));
            const horaSchedule = schedule.config.hora || '12:00';
            setHora(horaSchedule);
            setTimePickerValue(createTimeFromHora(horaSchedule));
          } else if (schedule.tipo === 'weekly') {
            // Asegurar que diasSemana sea un array válido
            const diasSemanaFromConfig = schedule.config.diasSemana;
            setDiasSemana(Array.isArray(diasSemanaFromConfig) ? diasSemanaFromConfig : ['1']);
            const horaSchedule = schedule.config.hora || '12:00';
            setHora(horaSchedule);
            setTimePickerValue(createTimeFromHora(horaSchedule));
          } else if (schedule.tipo === 'monthly') {
            setDiaMes(schedule.config.diaMes);
            const horaSchedule = schedule.config.hora || '12:00';
            setHora(horaSchedule);
            setTimePickerValue(createTimeFromHora(horaSchedule));
          }
        } else {
          setModoCreacion('scheduled');
          setFrecuencia('once');
          setAllowInstant(true); // Si no hay schedule definido, permitir ambos modos
        }

        // Configurar los emails - asegurar que sea un array válido
        const receptoresFromSchedule = schedule.receptores;
        setEmails(Array.isArray(receptoresFromSchedule) ? receptoresFromSchedule : []);

        setIsLoadingReport(false);
      } catch (error) {
        console.error('Error completo:', error);
        setError(`Error al cargar el reporte: ${error.message || 'Error desconocido'}`);
        setIsLoadingReport(false);
      }
    };

    fetchReport();
  }, [reportId]);
  
  // Handler para cambio de tipo de reporte por el usuario
  const handleTipoReporteChange = (nuevoTipo) => {
    setTipoReporte(nuevoTipo);
    const allowedFieldNames = getAllowedFieldNames(nuevoTipo);
    if (nuevoTipo === 'register') {
      setCamposSeleccionados(
        normalizeFieldSelection(CAMPOS_REGISTRO, allowedFieldNames)
      );
    } else if (nuevoTipo === 'activity') {
      setCamposSeleccionados(
        normalizeFieldSelection(CAMPOS_ACTIVIDAD, allowedFieldNames)
      );
    } else if (nuevoTipo === 'custom') {
      setCamposSeleccionados(
        normalizeFieldSelection(
          [...CAMPOS_REGISTRO, ...CAMPOS_ACTIVIDAD],
          allowedFieldNames
        )
      );
    }
  };
  
  // Efecto para cargar los datos de los filtros
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Cargar regiones
        const regionesResponse = await ReporteriaAPI.getRegions();
        setRegiones(regionesResponse.data);
        setIsLoadingRegiones(false);
      } catch (error) {
        setErrorRegiones('Error al cargar las regiones');
        setIsLoadingRegiones(false);
      }

      try {
        // Cargar roles
        const rolesResponse = await ReporteriaAPI.getRoles();
        setRoles(rolesResponse.data);
        setIsLoadingRoles(false);
      } catch (error) {
        setErrorRoles('Error al cargar los roles');
        setIsLoadingRoles(false);
      }

      try {
        // Cargar rubros
        const rubrosResponse = await ReporteriaAPI.getRubros();
        setRubros(rubrosResponse.data);
        setIsLoadingRubros(false);
      } catch (error) {
        setErrorRubros('Error al cargar los rubros');
        setIsLoadingRubros(false);
      }

      try {
        // Cargar géneros
        const generosResponse = await ReporteriaAPI.getGenders();
        setGeneros(generosResponse.data);
        setIsLoadingGeneros(false);
      } catch (error) {
        setErrorGeneros('Error al cargar los géneros');
        setIsLoadingGeneros(false);
      }

      try {
        // Cargar convocatorias
        const convocatoriasResponse = await ReporteriaAPI.getConvocatorias();
        setConvocatorias(convocatoriasResponse.data);
        setIsLoadingConvocatorias(false);
      } catch (error) {
        setErrorConvocatorias('Error al cargar las convocatorias');
        setIsLoadingConvocatorias(false);
      }
    };

    fetchFilterData();
  }, []);
  
  // Handler para cambio de campos seleccionados
  const handleCampoChange = (campo) => {
    if (camposSeleccionados.includes(campo)) {
      setCamposSeleccionados(camposSeleccionados.filter(c => c !== campo));
    } else {
      setCamposSeleccionados([...camposSeleccionados, campo]);
    }
  };
  
  // Handler para cambio de filtros
  const handleFiltroChange = (tipo, valor) => {
    if (filtros[tipo].includes(valor)) {
      setFiltros({
        ...filtros,
        [tipo]: filtros[tipo].filter(v => v !== valor)
      });
    } else {
      setFiltros({
        ...filtros,
        [tipo]: [...filtros[tipo], valor]
      });
    }
  };
  
  // Handler para cambio de días de la semana (selección múltiple)
  const handleDiaSemanaChange = (diaId) => {
    if (diasSemana.includes(diaId)) {
      setDiasSemana(diasSemana.filter(d => d !== diaId));
    } else {
      setDiasSemana([...diasSemana, diaId]);
    }
  };
  
  // Handler para establecer la hora actual
  const setHoraActual = () => {
    const ahora = new Date();
    const horas = ahora.getHours().toString().padStart(2, '0');
    const horaActual = `${horas}:00`;
    setHora(horaActual);
    setTimePickerValue(createTimeFromHora(horaActual));
  };
  
  // Handler para cambio de hora en TimePicker
  const handleTimeChange = (time) => {
    if (time) {
      // Forzar minutos a 00 siempre
      const adjustedTime = new Date(time);
      adjustedTime.setMinutes(0, 0, 0);
      
      const horaString = extractHoraFromTime(adjustedTime);
      setHora(horaString);
      setTimePickerValue(adjustedTime);
    }
  };
  
  // Handler para agregar emails
  const handleAgregarEmail = () => {
    if (nuevoEmail && !emails.includes(nuevoEmail) && validateEmail(nuevoEmail)) {
      setEmails([...emails, nuevoEmail]);
      setNuevoEmail('');
    }
  };
  
  // Validación simple de email
  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };
  
  // Handler para eliminar un email
  const handleEliminarEmail = (email) => {
    setEmails(emails.filter(e => e !== email));
  };
  
  // Handler para el envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    
    try {
      // Creamos el objeto JSON que se enviará al backend
      const reporteConfig = {
        name: reportName || `Reporte ${tipoReporte} - ${new Date().toLocaleDateString()}`,
        description: reportDescription,
        configuration: {
          dimension: 'Usuarios',
          tipo: tipoReporte,
          campos: camposSeleccionados,
          filtros: filtros
        },
        schedule: modoCreacion === 'instant' 
          ? {
              tipo: 'instant',
              config: {},
              receptores: emails
            }
          : {
          tipo: frecuencia,
          config: frecuencia === 'once' 
            ? { 
                    fecha: moment(fechaProgramada).format('YYYY-MM-DD'), 
                hora 
              } 
            : frecuencia === 'weekly' 
              ? { diasSemana, hora } 
              : frecuencia === 'monthly' 
                ? { diaMes, hora } 
                : null,
          receptores: emails
        }
      };
      
      console.log('Enviando nueva configuración:', reporteConfig);
      
      // Enviamos la configuración a la API
      const response = await ReporteriaAPI.updateReport(reportId, reporteConfig);
      
      // Mostramos mensaje de éxito
      setPreviewJson(response);
      
      // Mensaje diferente según el modo
      const message = modoCreacion === 'instant' 
        ? 'Reporte actualizado y generado exitosamente' 
        : 'Reporte actualizado exitosamente';
      
      // Redirigimos al listado después de guardar
      history.push('/reporteria', { 
        message, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error al actualizar el reporte:', error);
      setPreviewJson({
        error: true,
        mensaje: error.response?.data?.mensaje || 'Error al actualizar el reporte'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handler para volver al listado
  const handleVolver = () => {
    history.push('/reporteria');
  };
  
  // Determinar qué campos mostrar basado en el tipo de reporte
  const getCamposDisponibles = () => {
    if (tipoReporte === 'register') return CAMPOS_REGISTRO;
    if (tipoReporte === 'activity') return CAMPOS_ACTIVIDAD;
    return [...CAMPOS_REGISTRO, ...CAMPOS_ACTIVIDAD]; // custom
  };

  // Componente personalizado para mostrar el selector de fecha
  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <TextField
      variant="outlined"
      size="small"
      value={value}
      onClick={onClick}
      inputRef={ref}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarIcon color="primary" />
          </InputAdornment>
        ),
        readOnly: true
      }}
    />
  ));


  // Handler para duplicar el reporte
  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      // Creamos el objeto JSON que se enviará al backend
      const reporteConfig = {
        name: `${reportName} (Copia)` || `Reporte ${tipoReporte} - ${new Date().toLocaleDateString()} (Copia)`,
        description: reportDescription,
        configuration: {
          dimension: 'Usuarios',
          tipo: tipoReporte,
          campos: camposSeleccionados,
          filtros: filtros
        },
        schedule: {
          tipo: frecuencia,
          config: frecuencia === 'once' 
            ? { 
                fecha: moment(fechaProgramada).format('YYYY-MM-DD'), 
                hora 
              } 
            : frecuencia === 'weekly' 
              ? { diasSemana, hora } 
              : frecuencia === 'monthly' 
                ? { diaMes, hora } 
                : null,
          receptores: emails
        }
      };
      
      // Enviamos la configuración a la API para crear un nuevo reporte
      const response = await ReporteriaAPI.createReport(reporteConfig);
      
      // Redirigimos al listado después de crear
      history.push('/reporteria', { 
        message: 'Reporte duplicado exitosamente', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error al duplicar el reporte:', error);
      setError(error.response?.data?.mensaje || 'Error al duplicar el reporte');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Handler para abrir el diálogo de confirmación
  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  // Handler para cerrar el diálogo de confirmación
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  // Handler para confirmar y guardar cambios
  const handleConfirmAndSave = () => {
    handleCloseConfirmDialog();
    handleSubmit();
  };

  // Handler para cancelar programación
  const handleCancelSchedule = async () => {
    setIsCancelling(true);
    try {
      await ReporteriaAPI.cancelReportSchedule(reportId);
      history.push('/reporteria', {
        message: 'La programación del reporte fue cancelada exitosamente',
        type: 'success'
      });
    } catch (error) {
      setError('No se pudo cancelar la programación.');
    } finally {
      setIsCancelling(false);
      setOpenCancelDialog(false);
    }
  };

  // Funciones mejoradas para manejo de secciones con debug
  const getSectionFieldNames = (seccion) => {
    const fields = getCamposDisponibles()
      .filter(campo => campo.section === seccion)
      .map(campo => campo.name);
    console.log(`getSectionFieldNames(${seccion}):`, fields);
    return fields;
  };

  const getSectionState = (seccion) => {
    const fieldsInSection = getSectionFieldNames(seccion);
    const selectedFields = camposSeleccionados.filter(campo => fieldsInSection.includes(campo));
    
    const allSelected = fieldsInSection.length > 0 && selectedFields.length === fieldsInSection.length;
    const noneSelected = selectedFields.length === 0;
    const someSelected = selectedFields.length > 0 && selectedFields.length < fieldsInSection.length;
    
    console.log(`getSectionState(${seccion}):`, {
      fieldsInSection: fieldsInSection.length,
      selectedFields: selectedFields.length,
      allSelected,
      noneSelected, 
      someSelected,
      camposSeleccionados: camposSeleccionados.slice()
    });
    
    return {
      allSelected,
      noneSelected, 
      someSelected,
      fieldsInSection,
      selectedCount: selectedFields.length,
      totalCount: fieldsInSection.length
    };
  };

  const handleSectionToggle = (seccion) => {
    const fieldsInSection = getSectionFieldNames(seccion);
    setCamposSeleccionados((prev) => {
      const allSelected = fieldsInSection.every(campo => prev.includes(campo));
      if (allSelected) {
        const newSelection = prev.filter(campo => !fieldsInSection.includes(campo));
        console.log(`handleSectionToggle: deseleccionando ${seccion}`, { newSelection });
        return newSelection;
      } else {
        const newSelection = [...new Set([...prev, ...fieldsInSection])];
        console.log(`handleSectionToggle: seleccionando ${seccion}`, { newSelection });
        return newSelection;
      }
    });
  };

  const getEstadoSeccion = (seccion) => {
    const camposSeccion = getCamposDisponibles()
      .filter(campo => campo.section === seccion)
      .map(campo => campo.name);
    
    const todosSeleccionados = camposSeccion.every(name => camposSeleccionados.includes(name));
    const ningunoSeleccionado = camposSeccion.every(name => !camposSeleccionados.includes(name));
    
    return {
      campos: camposSeccion,
      todosSeleccionados,
      indeterminado: !todosSeleccionados && !ningunoSeleccionado
    };
  };
  
  const handleSeccionChange = (seccion) => {
    const { campos, todosSeleccionados } = getEstadoSeccion(seccion);
    
    if (todosSeleccionados) {
      // Si todos están seleccionados, deseleccionar todos los campos de esta sección
      setCamposSeleccionados(camposSeleccionados.filter(campo => !campos.includes(campo)));
    } else {
      // Si están parcialmente seleccionados o ninguno seleccionado, seleccionar todos
      setCamposSeleccionados([...new Set([...camposSeleccionados, ...campos])]);
    }
  };
  // Si está cargando el reporte, mostrar un spinner
  if (isLoadingReport) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Si hay un error al cargar el reporte, mostrar mensaje de error
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <div className="user-container reporteria-container">
      <Card className="card-container" style={{ marginBottom: 24 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" padding={2}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleVolver} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" className="report-title" style={{ marginLeft: 8 }}>
              Editar Reporte
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="secondary"
              style={{ borderRadius: 20, padding: '8px 24px', backgroundColor: '#e74c3c', color: '#fff', marginRight: 16 }}
              onClick={() => setOpenCancelDialog(true)}
              disabled={isCancelling}
            >
              Cancelar Programación
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="actions-table__button"
              startIcon={<FileCopyIcon />}
              onClick={handleDuplicate}
              disabled={isDuplicating}
              style={{ borderRadius: 8, padding: '8px 24px', backgroundColor: '#38bdf8', color: '#fff', marginRight: 16 }}
            >
              Duplicar Reporte
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="actions-table__button"
              startIcon={<SaveIcon />}
              onClick={handleOpenConfirmDialog}
              disabled={isLoading}
              style={{ borderRadius: 8, padding: '8px 24px', backgroundColor: '#38bdf8', color: '#fff' }}
            >
              Guardar Cambios
            </Button>
          </Box>
        </Box>
      </Card>

      <Paper className="report-form-container" style={{ padding: 24, marginBottom: 24, borderRadius: 8 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Información del reporte */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Información del reporte
                </FormLabel>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormLabel component="legend" style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'rgba(0, 0, 0, 0.7)' }}>
                      Nombre del reporte
                    </FormLabel>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                      helperText="Si no especificas un nombre, se generará uno automáticamente"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormLabel component="legend" style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'rgba(0, 0, 0, 0.7)' }}>
                      Descripción (opcional)
                    </FormLabel>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={reportDescription}
                      onChange={(e) => setReportDescription(e.target.value)}
                      multiline
                      rows={3}
                      helperText="Agrega una descripción para identificar fácilmente este reporte"
                    />
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>

            {/* Dimensión del reporte (fija) */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Dimensión del reporte
                </FormLabel>
                <Box mt={1} p={2} bgcolor="#f8f8f8" borderRadius={4}>
                  <Typography variant="body1">Usuarios</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Esta dimensión está fija por el momento
                  </Typography>
                </Box>
              </FormControl>
            </Grid>

            {/* Tipo de reporte */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Tipo de reporte
                </FormLabel>
                <RadioGroup
                  value={tipoReporte}
                  onChange={(e) => handleTipoReporteChange(e.target.value)}
                >
                  <FormControlLabel 
                    value="register" 
                    control={<Radio color="primary" />} 
                    label="Registro (datos personales y de registro del usuario)" 
                  />
                  <FormControlLabel 
                    value="activity" 
                    control={<Radio color="primary" />} 
                    label="Actividad (actividades realizadas en la plataforma)" 
                  />
                  <FormControlLabel 
                    value="custom" 
                    control={<Radio color="primary" />} 
                    label="Todos los campos (incluye datos de registro y actividad)" 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Selección de campos */}
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333', marginLeft: 2 }}>
                    Selección de campos
                  </FormLabel>
                  <Typography variant="caption" color="textSecondary" paragraph style={{ marginLeft: 2 }}>
                    Selecciona los campos que deseas incluir en el reporte.
                  </Typography>
                  { (tipoReporte == 'register' || tipoReporte == 'custom') && (
                    <>
                      {/* Datos Personales */}
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={getSectionState('Datos Personales').allSelected}
                            onChange={() => handleSectionToggle('Datos Personales')}
                          />
                        }
                        label={<Typography variant="subtitle1" style={{ fontWeight: 500, marginLeft: 4 }}>Datos Personales</Typography>}
                        style={{ width: '100%', justifyContent: 'center', marginLeft: 0 }}
                      />
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <Box mt={1} display="flex" flexWrap="wrap" p={2} bgcolor="#fafafa" borderRadius={4}>
                      {getCamposDisponibles().filter((campo) => campo.section === 'Datos Personales').map((campo) => (
                        <FormControlLabel
                        key={campo.name}
                        control={
                          <Checkbox
                          checked={camposSeleccionados.includes(campo.name)}
                          onChange={() => handleCampoChange(campo.name)}
                          color="primary"
                          />
                        }
                        label={campo.name}
                        className="report-field-option"
                        style={{ margin: 0 }}
                        />
                      ))}
                      </Box>
                      {/* Datos de Empresa */}
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={getSectionState('Datos de Empresa').allSelected}
                            onChange={() => handleSectionToggle('Datos de Empresa')}
                          />
                        }
                        label={<Typography variant="subtitle1" style={{ fontWeight: 500, marginLeft: 4 }}>Datos de Empresa</Typography>}
                        style={{ width: '100%', justifyContent: 'center', marginLeft: 0 }}
                      />
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <Box mt={1} display="flex" flexWrap="wrap" p={2} bgcolor="#fafafa" borderRadius={4}>
                      {getCamposDisponibles().filter((campo) => campo.section == 'Datos de Empresa').map((campo) => (
                        <FormControlLabel
                        key={campo.name}
                        control={
                          <Checkbox
                          checked={camposSeleccionados.includes(campo.name)}
                          onChange={() => handleCampoChange(campo.name)}
                          color="primary"
                          />
                        }
                        label={campo.name}
                        className="report-field-option"
                        style={{ margin: 0 }}
                        />
                      ))}
                      </Box>
                      {/* Datos de Mentor */}
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={getSectionState('Datos de Mentor').allSelected}
                            onChange={() => handleSectionToggle('Datos de Mentor')}
                          />
                        }
                        label={<Typography variant="subtitle1" style={{ fontWeight: 500, marginLeft: 4 }}>Datos de Mentor</Typography>}
                        style={{ width: '100%', justifyContent: 'center', marginLeft: 0 }}
                      />
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <Box mt={1} display="flex" flexWrap="wrap" p={2} bgcolor="#fafafa" borderRadius={4}>
                      {getCamposDisponibles().filter((campo) => campo.section === 'Datos de Mentor').map((campo) => (
                        <FormControlLabel
                        key={campo.name}
                        control={
                          <Checkbox
                          checked={camposSeleccionados.includes(campo.name)}
                          onChange={() => handleCampoChange(campo.name)}
                          color="primary"
                          />
                        }
                        label={campo.name}
                        className="report-field-option"
                        style={{ margin: 0 }}
                        />
                      ))}
                      </Box>
                    </>
                  )}

                  { (tipoReporte == 'activity' || tipoReporte == 'custom') && (
                    <>
                      {/* Datos de actividad */}
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={getSectionState('Actividad').allSelected}
                            onChange={() => handleSectionToggle('Actividad')}
                          />
                        }
                        label={<Typography variant="subtitle1" style={{ fontWeight: 500, marginLeft: 4 }}>Actividad</Typography>}
                        style={{ width: '100%', justifyContent: 'center', marginLeft: 0 }}
                      />
                      <Divider style={{ margin: '8px 0', backgroundColor: '#ddd' }} />
                      <Box mt={1} display="flex" flexWrap="wrap" p={2} bgcolor="#fafafa" borderRadius={4}>
                      {getCamposDisponibles().filter((campo) => campo.section == 'Actividad').map((campo) => (
                        <FormControlLabel
                        key={campo.name}
                        control={
                          <Checkbox
                          checked={camposSeleccionados.includes(campo.name)}
                          onChange={() => handleCampoChange(campo.name)}
                          color="primary"
                          />
                        }
                        label={campo.name}
                        className="report-field-option"
                        style={{ margin: 0 }}
                        />
                      ))}
                      </Box>
                    </>
                  )}
                </FormControl>
              </Grid>
            {/* Filtros */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Filtros
                </FormLabel>
                <Typography variant="caption" color="textSecondary" paragraph>
                  Aplica filtros opcionales para refinar los datos del reporte.
                </Typography>
                
                <Box sx={{ width: '100%' }}>
                  <Grid 
                    container 
                    spacing={3} 
                    style={{ 
                      margin: 0,
                      width: '100%'
                    }}
                  >
                    {/* Filtro de Rol */}
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      lg={3}
                      style={{ 
                        paddingLeft: 0,
                        paddingRight: '24px'
                      }}
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <FormLabel component="legend" style={{ marginBottom: 8 }}>Role</FormLabel>
                        <Select
                          multiple
                          value={filtros.role}
                          onChange={(e) => setFiltros({ ...filtros, role: e.target.value })}
                          renderValue={(selected) => (
                            <Box 
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                maxHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Seleccione opciones
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {selected.length === 1 
                                    ? selected[0] 
                                    : `${selected[0]} +${selected.length - 1}`}
                                </Typography>
                              )}
                            </Box>
                          )}
                          disabled={isLoadingRoles}
                          error={!!errorRoles}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PopperProps: {
                              strategy: 'fixed',
                              disablePortal: false,
                              modifiers: {
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: 'viewport',
                                },
                                flip: {
                                  enabled: true,
                                }
                              }
                            },
                          }}
                          style={{
                            height: '40px'
                          }}
                        >
                          {isLoadingRoles ? (
                            <MenuItem disabled>
                              <Typography>Cargando roles...</Typography>
                            </MenuItem>
                          ) : errorRoles ? (
                            <MenuItem disabled>
                              <Typography color="error">{errorRoles}</Typography>
                            </MenuItem>
                          ) : (
                            roles.map((role) => (
                              <MenuItem key={role.id} value={role.nombre}>
                                <Checkbox checked={filtros.role.includes(role.nombre)} />
                                {role.nombre}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Filtro de Rubro */}
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      lg={3}
                      style={{ 
                        paddingLeft: 0,
                        paddingRight: '24px'
                      }}
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <FormLabel component="legend" style={{ marginBottom: 8 }}>Rubro</FormLabel>
                        <Select
                          multiple
                          value={filtros.rubro}
                          onChange={(e) => setFiltros({ ...filtros, rubro: e.target.value })}
                          renderValue={(selected) => (
                            <Box 
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                maxHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Seleccione opciones
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {selected.length === 1 
                                    ? selected[0] 
                                    : `${selected[0]} +${selected.length - 1}`}
                                </Typography>
                              )}
                            </Box>
                          )}
                          disabled={isLoadingRubros}
                          error={!!errorRubros}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PopperProps: {
                              strategy: 'fixed',
                              disablePortal: false,
                              modifiers: {
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: 'viewport',
                                },
                                flip: {
                                  enabled: true,
                                }
                              }
                            },
                          }}
                          style={{
                            height: '40px'
                          }}
                        >
                          {isLoadingRubros ? (
                            <MenuItem disabled>
                              <Typography>Cargando rubros...</Typography>
                            </MenuItem>
                          ) : errorRubros ? (
                            <MenuItem disabled>
                              <Typography color="error">{errorRubros}</Typography>
                            </MenuItem>
                          ) : (
                            rubros.map((rubro) => (
                              <MenuItem key={rubro.id} value={rubro.nombre}>
                                <Checkbox checked={filtros.rubro.includes(rubro.nombre)} />
                                {rubro.nombre}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Filtro de Género */}
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      lg={3}
                      style={{ 
                        paddingLeft: 0,
                        paddingRight: '24px'
                      }}
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <FormLabel component="legend" style={{ marginBottom: 8 }}>Género</FormLabel>
                        <Select
                          multiple
                          value={filtros.genero}
                          onChange={(e) => setFiltros({ ...filtros, genero: e.target.value })}
                          renderValue={(selected) => (
                            <Box 
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                maxHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Seleccione opciones
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {selected.length === 1 
                                    ? selected[0] 
                                    : `${selected[0]} +${selected.length - 1}`}
                                </Typography>
                              )}
                            </Box>
                          )}
                          disabled={isLoadingGeneros}
                          error={!!errorGeneros}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PopperProps: {
                              strategy: 'fixed',
                              disablePortal: false,
                              modifiers: {
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: 'viewport',
                                },
                                flip: {
                                  enabled: true,
                                }
                              }
                            },
                          }}
                          style={{
                            height: '40px'
                          }}
                        >
                          {isLoadingGeneros ? (
                            <MenuItem disabled>
                              <Typography>Cargando géneros...</Typography>
                            </MenuItem>
                          ) : errorGeneros ? (
                            <MenuItem disabled>
                              <Typography color="error">{errorGeneros}</Typography>
                            </MenuItem>
                          ) : (
                            generos.map((genero) => (
                              <MenuItem key={genero.id} value={genero.nombre}>
                                <Checkbox checked={filtros.genero.includes(genero.nombre)} />
                                {genero.nombre}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* Filtro de Región */}
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      lg={3}
                      style={{ 
                        paddingLeft: 0,
                        paddingRight: 0
                      }}
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <FormLabel component="legend" style={{ marginBottom: 8 }}>Región</FormLabel>
                        <Select
                          multiple
                          value={filtros.region}
                          onChange={(e) => setFiltros({ ...filtros, region: e.target.value })}
                          renderValue={(selected) => (
                            <Box 
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                maxHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Seleccione opciones
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {selected.length === 1 
                                    ? selected[0] 
                                    : `${selected[0]} +${selected.length - 1}`}
                                </Typography>
                              )}
                            </Box>
                          )}
                          disabled={isLoadingRegiones}
                          error={!!errorRegiones}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PopperProps: {
                              strategy: 'fixed',
                              disablePortal: false,
                              modifiers: {
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: 'viewport',
                                },
                                flip: {
                                  enabled: true,
                                }
                              }
                            },
                          }}
                          style={{
                            height: '40px'
                          }}
                        >
                          {isLoadingRegiones ? (
                            <MenuItem disabled>
                              <Typography>Cargando regiones...</Typography>
                            </MenuItem>
                          ) : errorRegiones ? (
                            <MenuItem disabled>
                              <Typography color="error">{errorRegiones}</Typography>
                            </MenuItem>
                          ) : (
                            regiones.map((region) => (
                              <MenuItem key={region.id} value={region.nombre}>
                                <Checkbox checked={filtros.region.includes(region.nombre)} />
                                {region.nombre}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Filtro de Convocatorias */}
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      lg={3}
                      style={{ 
                        paddingLeft: 0,
                        paddingRight: 0
                      }}
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <FormLabel component="legend" style={{ marginBottom: 8 }}>Convocatorias</FormLabel>
                        <Select
                          multiple
                          value={filtros.convocatoria}
                          onChange={(e) => setFiltros({ ...filtros, convocatoria: e.target.value })}
                          renderValue={(selected) => (
                            <Box 
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                maxWidth: '100%',
                                maxHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              {selected.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                  Seleccione opciones
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {selected.length === 1 
                                    ? convocatorias.find(c => c.id === selected[0])?.nombre 
                                    : `${convocatorias.find(c => c.id === selected[0])?.nombre} +${selected.length - 1}`}
                                </Typography>
                              )}
                            </Box>
                          )}
                          disabled={isLoadingConvocatorias}
                          error={!!errorConvocatorias}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PopperProps: {
                              strategy: 'fixed',
                              disablePortal: false,
                              modifiers: {
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: 'viewport',
                                },
                                flip: {
                                  enabled: true,
                                }
                              }
                            },
                          }}
                          style={{
                            height: '40px'
                          }}
                        >
                          {isLoadingConvocatorias ? (
                            <MenuItem disabled>
                              <Typography>Cargando convocatorias...</Typography>
                            </MenuItem>
                          ) : errorRegiones ? (
                            <MenuItem disabled>
                              <Typography color="error">{errorConvocatorias}</Typography>
                            </MenuItem>
                          ) : (
                            convocatorias.map((convocatoria) => (
                              <MenuItem key={convocatoria.id} value={convocatoria.id}>
                                <Checkbox checked={filtros.convocatoria.includes(convocatoria.id)} />
                                {convocatoria.nombre}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </FormControl>
            </Grid>

            {/* Modo de creación - Toggle Switch */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Modo de creación
                </FormLabel>
                
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="flex-start"
                  style={{ 
                    backgroundColor: '#f8f9fa',
                    borderRadius: '50px',
                    padding: '8px',
                    width: 'fit-content',
                    border: '2px solid #e9ecef',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Fondo animado del toggle */}
                  <Box
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: modoCreacion === 'instant' ? '8px' : '50%',
                      width: 'calc(50% - 8px)',
                      height: 'calc(100% - 16px)',
                      backgroundColor: '#38bdf8',
                      borderRadius: '40px',
                      transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 0,
                      pointerEvents: 'none'
                    }}
                  />
                  
                  {/* Opción Crear Ahora */}
                  {allowInstant ? (
                    <Box
                      onClick={() => setModoCreacion('instant')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 24px',
                        borderRadius: '40px',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 1,
                        transition: 'color 0.3s ease',
                        color: modoCreacion === 'instant' ? '#fff' : '#666',
                        fontWeight: modoCreacion === 'instant' ? 600 : 400,
                        minWidth: '160px',
                        justifyContent: 'center'
                      }}
                    >
                      <FlashOnIcon 
                        style={{ 
                          marginRight: '8px', 
                          fontSize: '20px',
                          color: modoCreacion === 'instant' ? '#fff' : '#38bdf8'
                        }} 
                      />
                      Crear ahora
                    </Box>
                  ) : (
                    <Tooltip title="No se puede cambiar a 'Crear ahora' en reportes ya programados">
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 24px',
                          borderRadius: '40px',
                          position: 'relative',
                          zIndex: 1,
                          color: '#bbb',
                          fontWeight: 400,
                          minWidth: '160px',
                          justifyContent: 'center',
                          cursor: 'not-allowed',
                          opacity: 0.6,
                          pointerEvents: 'none'
                        }}
                      >
                        <FlashOnIcon style={{ marginRight: '8px', fontSize: '20px', color: '#bbb' }} />
                        Crear ahora
                      </Box>
                    </Tooltip>
                  )}
                  
                  {/* Opción Programar */}
                  <Box
                    onClick={() => setModoCreacion('scheduled')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 24px',
                      borderRadius: '40px',
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 1,
                      transition: 'color 0.3s ease',
                      color: modoCreacion === 'scheduled' ? '#fff' : '#666',
                      fontWeight: modoCreacion === 'scheduled' ? 600 : 400,
                      minWidth: '160px',
                      justifyContent: 'center'
                    }}
                  >
                    <ScheduleIcon 
                      style={{ 
                        marginRight: '8px', 
                        fontSize: '20px',
                        color: modoCreacion === 'scheduled' ? '#fff' : '#38bdf8'
                      }} 
                    />
                    Programar
                  </Box>
                </Box>
                
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                  {modoCreacion === 'instant' 
                    ? 'El reporte se generará inmediatamente al guardar'
                    : 'Configura cuándo y con qué frecuencia se debe generar el reporte'
                  }
                </Typography>
              </FormControl>
            </Grid>

            {/* Frecuencia - Solo mostrar si el modo es 'scheduled' */}
            <Grid item xs={12}>
              <Collapse in={modoCreacion === 'scheduled'} timeout={400}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  Frecuencia
                </FormLabel>
                <RadioGroup
                  value={frecuencia}
                  onChange={(e) => setFrecuencia(e.target.value)}
                    row={false}
                    style={{ alignItems: 'flex-start', flexDirection: 'column' }}
                >
                  <FormControlLabel 
                    value="once" 
                    control={<Radio color="primary" />} 
                    label="Una sola vez" 
                      style={{ marginRight: 32, marginLeft: 0, padding: 0, flex: 'none' }}
                  />
                  <FormControlLabel 
                    value="weekly" 
                    control={<Radio color="primary" />} 
                    label="Envío semanal" 
                      style={{ marginRight: 32, marginLeft: 0, padding: 0, flex: 'none' }}
                  />
                  <FormControlLabel 
                    value="monthly" 
                    control={<Radio color="primary" />} 
                    label="Envío mensual" 
                      style={{ marginRight: 0, marginLeft: 0, padding: 0, flex: 'none' }}
                  />
                </RadioGroup>
                
                {/* Configuración para frecuencia "una sola vez" - Selección de fecha y hora */}
                {frecuencia === 'once' && (
                  <Box ml={4} mt={1}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>Fecha programada</FormLabel>
                          <DatePicker
                            selected={fechaProgramada}
                            onChange={(date) => setFechaProgramada(date)}
                            customInput={<CustomDateInput />}
                            dateFormat="dd/MM/yyyy"
                            shouldCloseOnSelect
                            minDate={new Date()}
                            placeholderText="Seleccione una fecha"
                            popperProps={{ positionFixed: true }}
                            popperPlacement="bottom"
                            popperClassName="calendar-popper"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>Hora</FormLabel>
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                              <TimePicker
                                value={timePickerValue}
                                onChange={handleTimeChange}
                                format="HH:mm"
                                views={['hours']}
                                openTo="hours"
                                minutesStep={60}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTimeIcon color="primary" />
                                </InputAdornment>
                              ),
                                  style: { height: '40px' }
                            }}
                                style={{ width: '100%' }}
                          />
                            </MuiPickersUtilsProvider>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>Acción</FormLabel>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={setHoraActual}
                            startIcon={<AccessTimeIcon />}
                            fullWidth
                            style={{ height: '40px' }}
                          >
                            Hora actual
                          </Button>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                
                {/* Configuración para frecuencia semanal - Selección múltiple de días */}
                {frecuencia === 'weekly' && (
                  <Box ml={4} mt={1}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" style={{ marginBottom: 8 }}>
                        Selecciona los días de la semana
                      </FormLabel>
                      <Box display="flex" flexWrap="wrap" mb={2} p={2} bgcolor="#fafafa" borderRadius={4} minHeight="50px">
                        {DIAS_SEMANA.map((dia) => (
                          <Chip
                            key={dia.id}
                            label={dia.label}
                            onClick={() => handleDiaSemanaChange(dia.id)}
                            color={diasSemana.includes(dia.id) ? "primary" : "default"}
                            style={{ margin: '0 8px 8px 0', cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                      
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth variant="outlined" size="small">
                            <FormLabel component="legend" style={{ marginBottom: 8 }}>Hora</FormLabel>
                              <MuiPickersUtilsProvider utils={MomentUtils}>
                                <TimePicker
                                  value={timePickerValue}
                                  onChange={handleTimeChange}
                                  format="HH:mm"
                                  views={['hours']}
                                  openTo="hours"
                                  minutesStep={60}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AccessTimeIcon color="primary" />
                                  </InputAdornment>
                                ),
                                    style: { height: '40px' }
                              }}
                                  style={{ width: '100%' }}
                            />
                              </MuiPickersUtilsProvider>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth variant="outlined" size="small">
                            <FormLabel component="legend" style={{ marginBottom: 8 }}>Acción</FormLabel>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={setHoraActual}
                              startIcon={<AccessTimeIcon />}
                              fullWidth
                              style={{ height: '40px' }}
                            >
                              Hora actual
                            </Button>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </FormControl>
                  </Box>
                )}
                
                {/* Configuración para frecuencia mensual - Selección simple del día del mes */}
                {frecuencia === 'monthly' && (
                  <Box ml={4} mt={1}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          height="76px"
                        >
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>
                            Día del mes
                          </FormLabel>
                          <Select
                            value={diaMes}
                            onChange={(e) => setDiaMes(e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{
                              style: {
                                padding: '8px 14px',
                                height: '24px',
                              }
                            }}
                            style={{
                              height: '40px'
                            }}
                            MenuProps={{
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              PopperProps: {
                                strategy: 'fixed',
                              },
                            }}
                          >
                            {DIAS_MES.map((dia) => (
                              <MenuItem key={dia} value={dia}>
                                <Box display="flex" alignItems="center" width="100%">
                                  <Typography>
                                    {dia}
                                  </Typography>
                                  {!existeEnTodosLosMeses(dia) && (
                                    <Typography 
                                      variant="caption" 
                                      color="textSecondary" 
                                      style={{ marginLeft: 8 }}
                                    >
                                      (*)
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        {!existeEnTodosLosMeses(diaMes) && (
                          <Typography 
                            variant="caption" 
                            color="error" 
                            style={{ marginTop: 8 }}
                          >
                            (*) {getAdvertenciaDiaMes(diaMes)}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          height="76px"
                        >
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>
                            Hora
                          </FormLabel>
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                              <TimePicker
                                value={timePickerValue}
                                onChange={handleTimeChange}
                                format="HH:mm"
                                views={['hours']}
                                openTo="hours"
                                minutesStep={60}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTimeIcon color="primary" />
                                </InputAdornment>
                              ),
                                  style: { height: '40px' }
                            }}
                                style={{ width: '100%' }}
                          />
                            </MuiPickersUtilsProvider>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          height="76px"
                        >
                          <FormLabel component="legend" style={{ marginBottom: 8 }}>
                            Acción
                          </FormLabel>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={setHoraActual}
                            startIcon={<AccessTimeIcon />}
                            fullWidth
                            style={{ 
                              height: '40px'
                            }}
                          >
                            Hora actual
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </FormControl>
              </Collapse>
            </Grid>

            {/* Receptores */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
                  {modoCreacion === 'instant' ? 'Receptores (Opcional)' : 'Receptores (Opcional)'}
                </FormLabel>
                <Typography variant="caption" color="textSecondary" paragraph>
                  {modoCreacion === 'instant' 
                    ? 'Si agregas correos, el reporte generado se enviará automáticamente a estas direcciones.'
                    : 'Agrega los correos electrónicos que recibirán el reporte. Puedes dejarlo vacío si solo deseas descargar el reporte.'
                  }
                </Typography>
                
                <Box mb={2} display="flex">
                  <TextField
                    value={nuevoEmail}
                    onChange={(e) => setNuevoEmail(e.target.value)}
                    placeholder="ejemplo@dominio.com"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={nuevoEmail !== '' && !validateEmail(nuevoEmail)}
                    helperText={nuevoEmail !== '' && !validateEmail(nuevoEmail) ? 'Email inválido' : ''}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && validateEmail(nuevoEmail)) {
                        e.preventDefault();
                        handleAgregarEmail();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAgregarEmail}
                    disabled={!nuevoEmail || !validateEmail(nuevoEmail)}
                    style={{ marginLeft: 8, borderRadius: 20 }}
                  >
                    Agregar
                  </Button>
                </Box>
                
                <Box 
                  display="flex" 
                  flexWrap="wrap" 
                  p={2} 
                  bgcolor="#fafafa" 
                  borderRadius={4}
                  minHeight="50px"
                >
                  {emails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleEliminarEmail(email)}
                      color="primary"
                      style={{ margin: 4 }}
                    />
                  ))}
                  {emails.length === 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                      No se han agregado receptores
                    </Typography>
                  )}
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* Vista previa del JSON (solo para desarrollo) */}
      {previewJson && (
        <Paper className="json-preview-container" style={{ padding: 24, borderRadius: 8 }}>
          <Box p={2}>
            <Typography variant="h6" className="form-section-title" style={{ fontSize: 18, marginBottom: 12, color: '#333' }}>
              Vista previa del JSON (solo para desarrollo)
            </Typography>
            <Box mt={1} p={2} bgcolor="#f8f8f8" borderRadius={4} overflow="auto" maxHeight={400}>
              <pre>{JSON.stringify(previewJson, null, 2)}</pre>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Diálogo de confirmación */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Estás seguro de que deseas guardar los cambios?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            La configuración actual del reporte será sobrescrita. Si deseas mantener la configuración original, 
            considera usar la opción "Duplicar Reporte" para crear una nueva versión.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} style={{ color: '#38bdf8', backgroundColor: '#fff', borderRadius: 20, padding: '8px 24px' }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmAndSave} style={{ backgroundColor: '#38bdf8', color: '#fff', borderRadius: 8, padding: '8px 24px' }} variant="contained" autoFocus>
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para cancelar programación */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          {"¿Cancelar programación del reporte?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Esta acción detendrá el envío automático de este reporte y marcará su ciclo como completado. ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)} style={{ color: '#38bdf8', backgroundColor: '#fff', borderRadius: 20, padding: '8px 24px' }}>
            No, mantener programación
          </Button>
          <Button onClick={handleCancelSchedule} style={{ backgroundColor: '#e74c3c', color: '#fff', borderRadius: 20, padding: '8px 24px' }} variant="contained" autoFocus disabled={isCancelling}>
            Sí, cancelar programación
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingModal open={isLoading || isDuplicating} />
    </div>
  );
};

export default ReportEditContainer;
